import React, { FC, useEffect } from 'react';
import { compact, map, uniq } from 'lodash';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import none from 'file-loader!@/assets/images/none.png'; // eslint-disable-line
import fallback from 'file-loader!@/assets/images/fallback-sample.png'; // eslint-disable-line
import { PropsWithClassName } from '@/types/common';
import {
  ConfigurableProduct,
  VariationView,
  ConfiguredProductCharacteristic,
} from '@/types/product';
import SampleSelector, { SampleOption } from '@/components/Common/SampleSelector';
import { useFormState } from '@/hooks/useFormState';
import { SelectField, TextField } from '@/components/Common/Field';
import { Option } from '@/types/utils';
import * as styles from './ProductCharacteristicSelector.module.scss';

enum KnownFinishGroupCode {
  Plugs = 'PM',
  Plugs2 = 'PM2',
  Plugs3 = 'PJ',
  Plugs4 = 'PN',
  NoFinish = 'N0',
}

const isPlug = (finishGroupCode: string) =>
  [
    KnownFinishGroupCode.Plugs,
    KnownFinishGroupCode.Plugs2,
    KnownFinishGroupCode.Plugs3,
    KnownFinishGroupCode.Plugs4,
  ].includes(finishGroupCode as any);

type Props = PropsWithClassName<{
  product: ConfigurableProduct;
  characteristicIndex: number;
  view: VariationView;
  value: ConfiguredProductCharacteristic; // there is always a default value
  onChange: (value: ConfiguredProductCharacteristic) => void;
  showSearchBoxOptionCountThreshold?: number;
}>;

const ProductCharacteristicSelector: FC<Props> = ({
  className,
  product,
  characteristicIndex,
  value,
  onChange,
  showSearchBoxOptionCountThreshold = 6,
}) => {
  const { i18n } = useLingui();
  const finishGroup = product.finishGroups[characteristicIndex];

  const options = compact(
    [...(finishGroup?.finishes ?? [])]
      ?.sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
      ?.map((finish) =>
        finish?.code
          ? {
              code: finish.code,
              name: finish.name ?? finish.code,
              position: finish.position ?? 0,
              titleGroup: finish.titleGroup ?? 'nada',
              image:
                finish?.thumbnail?.childImageSharp?.gatsbyImageData ??
                finish?.image?.childImageSharp?.gatsbyImageData ??
                fallback,
            }
          : null
      ) ?? []
  );
  const selectedOption = options.find((o) => o.code === value);

  useEffect(() => {
    if (!finishGroup?.code || finishGroup?.code === KnownFinishGroupCode.NoFinish) {
      onChange(options[0]?.code || null);
    }
  }, [finishGroup]);

  const noneOption: SampleOption = { code: null, name: i18n._(t`None`), image: none };
  const subgroups = uniq(map(options ?? [], 'titleGroup'));
  const { values, connect } = useFormState({
    subgroup: '',
    search: '',
  });
  const hasManySubgroups = subgroups.length > 1;
  const hasManyOptions = options.length > showSearchBoxOptionCountThreshold;
  const shouldShowFilters = hasManySubgroups || hasManyOptions;

  const filteredOptions = options
    .filter((option) => !values.subgroup || option.titleGroup === values.subgroup)
    .filter((option) => {
      const search = values.search?.toLowerCase();
      const code = option.code?.toLowerCase();
      const name = option.name?.toLowerCase();
      return !search || name?.includes(search) || code?.includes(search);
    });
  return !finishGroup ? (
    <div className={className}>
      <SampleSelector
        options={[noneOption]}
        selectedOption={noneOption}
        onChange={(option) => onChange(option.code)}
      />
    </div>
  ) : finishGroup?.code !== KnownFinishGroupCode.NoFinish ? (
    <>
      {shouldShowFilters ? (
        <div className={styles.filters}>
          {hasManySubgroups ? (
            <SelectField
              className={styles.subgroup}
              label={i18n._(t`Filter by`)}
              {...connect('subgroup')}
              options={[
                ['', ''],
                ...subgroups.map((subgroup): Option => [subgroup ?? '', subgroup ?? '']),
              ]}
            />
          ) : null}
          {hasManyOptions ? (
            <TextField
              className={styles.search}
              label={i18n._(t`Search`)}
              {...connect('search')}
            />
          ) : null}
        </div>
      ) : null}
      <div className={className}>
        <SampleSelector
          isCompact={!!finishGroup?.code && isPlug(finishGroup?.code)}
          options={filteredOptions}
          selectedOption={selectedOption}
          onChange={(option) => {
            onChange(option.code);
          }}
        />
      </div>
    </>
  ) : null;
};

export default ProductCharacteristicSelector;
